import { useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { ACTION_HINTS, ACTION_LABELS, governanceError, todayIso } from "../../lib/governance";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const NO_OPTION = "__none__";
const DONE_TOASTS = { adopt: "Décision adoptée", defer: "Décision différée : elle reste à trancher", cancel: "Décision annulée", repropose: "Décision remise en discussion" };

// One dialog for the four gestures of the lifecycle; the fields shown are the ones the gesture asks for, no more.
export function ActionDialog({ action, decision, onClose, onDone }) {
  const [form, setForm] = useState(() => ({
    decision: decision.decision || "", justification: decision.justification || "", chosen_option_id: decision.chosen_option_id || "",
    decided_on: todayIso(), review_on: "", reason: "",
  }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const needsReason = action !== "adopt";
  const problem = (() => {
    if (action === "adopt") return form.decision.trim().length < 3 ? "Écrivez la décision retenue." : form.justification.trim().length < 3 ? "Indiquez la justification." : "";
    if (action === "defer" && !form.review_on) return "Indiquez la date de revue.";
    if (needsReason && form.reason.trim().length < 3) return "Indiquez le motif.";
    return "";
  })();
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const body = { expected_version: decision.version };
      if (action === "adopt") Object.assign(body, { decision: form.decision.trim(), justification: form.justification.trim(), chosen_option_id: form.chosen_option_id || null, decided_on: form.decided_on || null });
      if (action === "defer") body.review_on = form.review_on;
      if (needsReason) body.reason = form.reason.trim();
      const { data } = await api.post(`/governance/decisions/${decision.id}/${action}`, body);
      toast.success(DONE_TOASTS[action]);
      onDone(data);
    } catch (e) {
      setError(governanceError(e, "Impossible de continuer. Réessayez."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent data-testid={`decision-dialog-${action}`}>
        <DialogHeader>
          <DialogTitle>{ACTION_LABELS[action]} — {decision.number}</DialogTitle>
          <DialogDescription>{ACTION_HINTS[action]}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {action === "adopt" && (
            <>
              {decision.options.length > 0 && (
                <div className="space-y-1.5"><Label>Option retenue</Label>
                  <Select value={form.chosen_option_id || NO_OPTION} onValueChange={(v) => set({ chosen_option_id: v === NO_OPTION ? "" : v })}>
                    <SelectTrigger className="h-10" aria-label="Option retenue" data-testid="adopt-option"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value={NO_OPTION}>Aucune des options</SelectItem>{decision.options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent>
                  </Select></div>
              )}
              <div className="space-y-1.5"><Label htmlFor="dec-text">Décision retenue</Label>
                <Textarea id="dec-text" rows={3} maxLength={4000} value={form.decision} onChange={(e) => set({ decision: e.target.value })} data-testid="adopt-decision" /></div>
              <div className="space-y-1.5"><Label htmlFor="dec-why">Justification</Label>
                <Textarea id="dec-why" rows={3} maxLength={4000} value={form.justification} onChange={(e) => set({ justification: e.target.value })} data-testid="adopt-justification" /></div>
              <div className="space-y-1.5"><Label htmlFor="dec-date">Date de la décision</Label>
                <Input id="dec-date" type="date" max={todayIso()} value={form.decided_on} onChange={(e) => set({ decided_on: e.target.value })} className="h-10 sm:w-48" data-testid="adopt-date" /></div>
            </>
          )}
          {action === "defer" && (
            <div className="space-y-1.5"><Label htmlFor="dec-review">Date de revue</Label>
              <Input id="dec-review" type="date" min={todayIso()} value={form.review_on} onChange={(e) => set({ review_on: e.target.value })} className="h-10 sm:w-48" data-testid="defer-review" /></div>
          )}
          {needsReason && (
            <div className="space-y-1.5"><Label htmlFor="dec-reason">Motif</Label>
              <Textarea id="dec-reason" rows={3} maxLength={1000} value={form.reason} onChange={(e) => set({ reason: e.target.value })} data-testid="decision-reason" /></div>
          )}
          {error && <p className="text-sm text-red-600" role="alert" data-testid="decision-action-error">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Retour</Button>
          <Button variant={action === "cancel" ? "destructive" : "default"} onClick={submit} disabled={busy || !!problem} title={problem || undefined} data-testid="decision-action-submit">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{ACTION_LABELS[action]}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
